import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { useConfirmDialog } from '@bluheadless/ui-logic/src/hooks/confirm-dialog'
import { useCatalogUrl } from '@bluheadless/ui-logic/src/hooks/useCatalogUrl'
import { useProductOptionsForQueryParams } from '@bluheadless/ui-logic/src/hooks/product/useProductOptionsForQueryParams'
import { useNotification } from '@bluheadless/ui-logic/src/hooks/useNotification'
import { useProductContext } from '@bluheadless/ui-logic/src/providers/product'
import { useQuickBuy } from '@bluheadless/ui-logic/src/providers/quick-buy'
import {
	PRODUCT_TYPE_BUNDLE,
	PRODUCT_TYPE_CONFIGURABLE,
	PRODUCT_TYPE_GIFTCARD,
	PRODUCT_TYPE_GROUPED,
} from '@bluheadless/ui-logic/src/constants'
import { elementType } from 'prop-types'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useProductPriceProps } from '../../product-price'
import TileCartMiddleware from './tile-cart-middleware'
import { useTrackingsActions } from '@bluheadless/ui-logic/src/hooks/trackings'
import { usePageContext } from '@bluheadless/ui-logic/src/providers/page/page'
import { useUserUniqueId } from '@bluheadless/ui-logic/src/hooks/useUserUniqueId'
import { useUser } from '@bluheadless/ui-logic/src/providers/user'
import { useCurrency } from '@bluheadless/ui-logic/src/providers/currency'

const CAN_EDIT_CART = [PRODUCT_TYPE_BUNDLE, PRODUCT_TYPE_GROUPED, PRODUCT_TYPE_GIFTCARD, PRODUCT_TYPE_CONFIGURABLE]
const FREE_ITEM = ['free_gift']
const CHEAPEST_FOR_FREE = ['cheapest-for-free']
const USE_CART_PRICE = [PRODUCT_TYPE_BUNDLE]

const TileCartBridge = ({
	cartItemId,
	priceProps,
	salesRule,
	totals,
	variant: tileVariant,
	onClick,
	pageContext,
	pageId: _pageId,
	widgetContext,
	...props
}) => {
	const [
		{
			product,
			selectedProduct,
			loadingWishList, // loading Add/Remove WishList
			loadingCart,
			isInWishList,
			qty, // cart qty
			assets,
			inventory: { allowBackOrders, inStock },
			selectedOptions,
			selectedOptionsIdsValues,
			configurableChildrenProducts,
			groupedChildrenProducts,
			bundleProducts,
		},
		{ cartChangeQty, addToWishList, removeFromWishList, removeFromCart },
	] = useProductContext()

	const { configurableOptions = [], bundleOptions = [], prices } = product

	const productOptionsForQueryParams = useProductOptionsForQueryParams({
		selectedOptionsIdsValues,
		configurableOptions,
		bundleOptions,
	})

	const {
		cart: { backOrderChip, freeItemChip },
		catalog: {
			products: {
				images: { height: height, width: width },
			},
		},
	} = useConfig()

	const { name, type, urlKey } = product
	const productUrl = useCatalogUrl(urlKey, productOptionsForQueryParams)

	const { enqueueNotification } = useNotification()
	const { isConfirmed } = useConfirmDialog()

	const [, { openQuickBuy }] = useQuickBuy()
	const { productClick } = useTrackingsActions()
	const { entityType: pageType, __bhEntityId: pageId } = usePageContext()
	const userUniqueId = useUserUniqueId()
	const [{ isLoggedIn }] = useUser()

	const productPriceProps = useProductPriceProps(priceProps)

	const { formatMessage } = useIntl()

	const handleDecrement = useCallback(async () => {
		try {
			await cartChangeQty({ qty: qty - 1 })
		} catch (error) {
			enqueueNotification({
				uiProps: { message: formatMessage({ id: 'wishlist_cant_update_qty' }), severity: 'error' },
			})
		}
	}, [enqueueNotification, cartChangeQty, qty, formatMessage])

	const handleIncrement = useCallback(async () => {
		try {
			await cartChangeQty({ qty: qty + 1 })
		} catch (error) {
			enqueueNotification({
				uiProps: { message: formatMessage({ id: 'wishlist_cant_update_qty' }), severity: 'error' },
			})
		}
	}, [enqueueNotification, cartChangeQty, qty, formatMessage])

	const handleRemoveFromCart = async () => {
		const confirmed = await isConfirmed(formatMessage({ id: 'cart_confirm_remove_item' }, { productName: name }))
		if (confirmed) {
			try {
				await removeFromCart({ id: cartItemId })
				enqueueNotification({ uiProps: { message: formatMessage({ id: 'deleted_success' }) } })
			} catch (error) {
				enqueueNotification({
					uiProps: { severity: 'error', message: formatMessage({ id: 'cart_cant_remove_item' }) },
				})
			}
		}
	}

	const handleAddToWishList = useCallback(async () => {
		try {
			await addToWishList()
		} catch (error) {
			enqueueNotification({
				uiProps: {
					severity: 'error',
					message: formatMessage({ id: 'wishlist_cant_add_item' }, { productName: name }),
				},
			})
		}
	}, [addToWishList, enqueueNotification, formatMessage, name])

	const handleRemoveFromWishList = useCallback(async () => {
		try {
			await removeFromWishList()
		} catch (error) {
			enqueueNotification({
				uiProps: {
					severity: 'error',
					message: formatMessage({ id: 'wishlist_cant_remove_item' }, { productName: name }),
				},
			})
		}
	}, [name, removeFromWishList, enqueueNotification, formatMessage])

	const isFreeItem = FREE_ITEM.includes(salesRule?.type)
	const isCheapestForFree = CHEAPEST_FOR_FREE.includes(salesRule?.type)
	const { currency } = useCurrency()

	return (
		<TileCartMiddleware
			cartRemoveProps={{
				disabled: loadingCart,
				loading: loadingCart,
				onClick: handleRemoveFromCart,
				hidden: (isFreeItem || isCheapestForFree) && salesRule?.autoAdd,
			}}
			editProps={
				CAN_EDIT_CART.includes(type) && tileVariant === 'base'
					? {
							disabled: loadingCart,
							loading: loadingCart,
							onClick: () =>
								openQuickBuy({
									product,
									action: 'cartItemUpdate',
									actionLabel: formatMessage({ id: 'action_update_cart_item' }),
									formDefaultValues: selectedOptions,
									defaultQty: qty,
									linkedCartItemId: cartItemId,
									pageContext: pageContext ?? pageType,
									pageId: _pageId ?? pageId,
									widgetContext,
								}),
					  }
					: {
							disabled: true,
							hidden: (isFreeItem || isCheapestForFree) && salesRule?.autoAdd,
					  }
			}
			backOrdersChipProps={{
				enabled: allowBackOrders,
				label: formatMessage({ id: 'back_order' }),
				color: backOrderChip?.color,
				textColor: backOrderChip?.textColor,
			}}
			freeItemChipProps={{
				enabled: isFreeItem,
				label: formatMessage({ id: 'cart_gift_label' }),
				color: freeItemChip?.color,
				textColor: freeItemChip?.textColor,
			}}
			priceProps={{
				currency: productPriceProps.currency,
				currencyDisplay: productPriceProps.currencyDisplay,
				currencyPosition: productPriceProps.currencyPosition,
			}}
			productProps={{
				assets,
				product: selectedProduct ?? product,
				qty,
				imageHeight: height,
				imageWidth: width,
				inStock,
			}}
			qtyProps={{
				editable: !(isFreeItem || isCheapestForFree),
				loading: loadingCart,
				decrement: handleDecrement,
				increment: handleIncrement,
				value: qty,
			}}
			totals={{
				price: !isFreeItem
					? totals?.price
						? USE_CART_PRICE.indexOf(type) > -1
							? totals?.price
							: prices.base
						: null
					: null,
				specialAmount: !isFreeItem
					? totals?.price
						? USE_CART_PRICE.indexOf(type) > -1
							? totals?.price
							: prices.final
						: null
					: null,
				subtotal: !isFreeItem ? totals?.subtotal : null,
			}}
			url={!isFreeItem ? productUrl : null}
			onClick={(e) => {
				productClick({
					params: {
						product,
						position: props.position,
						userUniqueId,
						currency,
						groupedChildrenProducts,
						configurableChildrenProducts,
						bundleProducts,
						isLoggedIn,
					},
					pageContext: pageContext ?? pageType,
					pageId: _pageId ?? pageId,
					widgetContext,
				})
				if (onClick) {
					onClick(e, { product })
				}
			}}
			wishListProps={{
				loading: loadingWishList,
				disabled: loadingWishList,
				added: isInWishList,
				onClick: isInWishList ? handleRemoveFromWishList : handleAddToWishList,
				hidden: isFreeItem || isCheapestForFree,
			}}
			{...props}
		/>
	)
}

TileCartBridge.defaultProps = {}

TileCartBridge.propTypes = {
	/**
	 * The component to render the tile with.
	 */
	Tile: elementType,
}

export default TileCartBridge
